// Account data rights (/v1/account): a JSON download of the caller's personal
// data and the deletion of the account. Both spend a fresh step-up grant
// (api/stepup.ts), because a stolen session alone must not be enough to take
// someone's data or to erase it. These routes, and the team deletion in
// api/teams.ts, exist only while the server has erasure turned on. With it off
// the GET preflight falls through to the SPA page and the writes answer 404,
// so the UI asks the preflight first and shows no control when it is absent.

import { optionalJSON, request, sendJSON } from "./http.js";

const BASE = "/v1/account";

/** SoleOwnerTeam is a team with other members that blocks the account
 * deletion: a shared team the caller alone owns, until another member becomes
 * an owner, or the caller's personal team, which goes with the account, until
 * the other members are removed. */
export interface SoleOwnerTeam {
  id: string;
  name: string;
  /** True for the caller's personal team. */
  personal: boolean;
  /** The number of members in the team. */
  members: number;
}

/** DeletedTeam is a team with no member but the caller, the personal team
 * among them. The account deletion deletes it together with its workspaces. */
export interface DeletedTeam {
  id: string;
  name: string;
  personal: boolean;
  workspaces: number;
}

/** AccountDeletion is the preflight: what deleting the account would do. */
export interface AccountDeletion {
  soleOwnerTeams: SoleOwnerTeam[];
  deletesTeams: DeletedTeam[];
  /** How many other teams the caller would leave; their data stays. */
  leavesTeams: number;
}

/**
 * ErasureResult answers an account or team deletion. `erased` means every
 * step was confirmed before the answer; `deleting` means the server finishes
 * the rest in the background. Either way the subject is gone for its users.
 */
export interface ErasureResult {
  erasureId: string;
  status: "erased" | "deleting";
}

/** getAccountDeletion loads the deletion preflight, or null when the server
 * has erasure off. */
export function getAccountDeletion(): Promise<AccountDeletion | null> {
  return optionalJSON<AccountDeletion>(`${BASE}/deletion`);
}

/**
 * erasureAvailable is the feature probe for pages that only add a control
 * (security, team). A failed probe counts as "off": those pages must work
 * without the control, and page-account reports real errors itself.
 */
export async function erasureAvailable(): Promise<boolean> {
  try {
    return (await getAccountDeletion()) !== null;
  } catch {
    return false;
  }
}

/** AccountExport is the downloaded file, named as the server suggests. */
export interface AccountExport {
  blob: Blob;
  filename: string;
}

/**
 * exportAccount downloads the caller's personal data as one JSON file.
 * `verification` is a step-up grant for `export_account`.
 */
export async function exportAccount(verification: string): Promise<AccountExport> {
  const res = await request(`${BASE}/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ verification }),
  });
  const named = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  const filename = named?.[1] ?? `bino-cloud-account-${new Date().toISOString().slice(0, 10)}.json`;
  return { blob: await res.blob(), filename };
}

/**
 * deleteAccount erases the caller's account. `verification` is a step-up
 * grant for `delete_account`. A 409 `sole_owner` carries the blocking teams
 * (ApiError.teams). On success the session is dead: the caller signs out.
 */
export function deleteAccount(verification: string): Promise<ErasureResult> {
  return sendJSON<ErasureResult>(BASE, "DELETE", { verification });
}
